import { useEffect, useState } from 'react';
import { useGetEvalFromMessageMutation } from 'services/api/conversationEvals.api';
import type { Client } from 'services/types/client';

export interface EvalSource {
  businessUnitIds: number[];
  mockClient: Partial<Client>;
  loading: boolean;
  failed: boolean;
}

/**
 * Business unit and client profile behind the sandbox session. Both come from the conversation the
 * playground was opened from — the one piece of eval context the sandbox itself never holds. On a
 * blank start there is no source conversation, so only the business unit picked in the gate is known.
 */
export const useEvalSource = (enabled: boolean, messageId?: string, businessUnitId?: number | null): EvalSource => {
  const [getEvalFromMessage] = useGetEvalFromMessageMutation();
  const [source, setSource] = useState<Omit<EvalSource, 'loading'>>({
    businessUnitIds: [],
    mockClient: {},
    failed: false,
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!enabled) {
      return;
    }
    if (!messageId) {
      setSource({ businessUnitIds: businessUnitId ? [businessUnitId] : [], mockClient: {}, failed: false });
      return;
    }
    setLoading(true);
    getEvalFromMessage({ messageId: Number(messageId) })
      .unwrap()
      .then((result) =>
        setSource({ businessUnitIds: result.businessUnitIds, mockClient: result.mockClient, failed: false })
      )
      .catch(() => setSource({ businessUnitIds: [], mockClient: {}, failed: true }))
      .finally(() => setLoading(false));
  }, [enabled, messageId, businessUnitId, getEvalFromMessage]);

  return { ...source, loading };
};
